import React, { useContext } from "react";
import { Box, Typography } from "@material-ui/core";
import { useLocation } from "react-router-dom";
import { FAQConnectorProps } from "./container";
import "./styles.scss";
import { FAQs } from "../../textConstants";
import { trackEvent } from "../../../../api/v1/analytics/trackEvent";
import {
  AccordionCollection,
  AccordionContent,
  Icon,
  useDeviceTypes,
} from "halifax";
import {
  VIEWED_OFFER_FAQ,
  SelectedTravelOfferScreen,
  CorpSessionInfo,
} from "redmond";
import { config } from "../../../../api/config";
import { ClientContext } from "../../../../App";

export interface IFAQProps extends FAQConnectorProps {}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const decodeSegment = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getFaqSlugFromRoute = (search: string, hash: string) => {
  const queryFaqSlug = new URLSearchParams(search).get("faq");
  if (queryFaqSlug) {
    return slugify(decodeSegment(queryFaqSlug));
  }

  if (hash.startsWith("#faq-")) {
    return slugify(decodeSegment(hash.replace("#faq-", "")));
  }
  if (hash.startsWith("#faq/")) {
    return slugify(decodeSegment(hash.replace("#faq/", "")));
  }
  if (hash.startsWith("#faq=")) {
    return slugify(decodeSegment(hash.replace("#faq=", "")));
  }
  return undefined;
};

export const FAQ = ({}: IFAQProps) => {
  const [trackEventFired, setTrackEventFired] = React.useState(false);
  const location = useLocation();

  const { matchesMobile } = useDeviceTypes();

  const { sessionInfo } = useContext(ClientContext);
  const isNonFinancialUser =
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role === "NFU";

  const trackFaqExpanded = (_: string, isExpanded: boolean) => {
    if (!trackEventFired && isExpanded) {
      trackEvent({
        eventName: VIEWED_OFFER_FAQ,
        properties: {
          screen: SelectedTravelOfferScreen.TRAVEL_WALLET_LANDING,
        },
      });
      setTrackEventFired(true);
    }
  };

  const faqContents = FAQs(
    config.TENANT,
    isNonFinancialUser
  ) as AccordionContent[];

  const defaultExpandedIndex = React.useMemo(() => {
    const deepLinkSlug = getFaqSlugFromRoute(location.search, location.hash);
    if (!deepLinkSlug) {
      return undefined;
    }

    return faqContents.findIndex(
      (faq) => slugify(typeof faq?.title === "string" ? faq.title : "") === deepLinkSlug
    );
  }, [faqContents, location.hash, location.search]);

  React.useEffect(() => {
    if (defaultExpandedIndex === undefined || defaultExpandedIndex < 0) {
      return;
    }

    window.setTimeout(() => {
      const accordionItem = document.querySelector(
        `.travel-wallet-credits-how-accordion .accordion-collection-child-content:nth-of-type(${
          defaultExpandedIndex + 1
        })`
      );

      accordionItem?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
  }, [defaultExpandedIndex]);

  return (
    <Box className="faq-container">
      <Typography variant={matchesMobile ? "h4" : "h2"} className="faq-title">
        Have questions? We’ve got answers.
      </Typography>
      <AccordionCollection
        accordionContents={faqContents}
        expandIcon={<Icon name="plus" />}
        collapseIcon={<Icon name="minus" />}
        onChange={trackFaqExpanded}
        defaultExpandedIndex={
          defaultExpandedIndex !== undefined && defaultExpandedIndex >= 0
            ? defaultExpandedIndex
            : undefined
        }
        className="travel-wallet-credits-how-accordion"
      />
    </Box>
  );
};
